import { useEffect, useRef, useState } from 'react';
import type { FinanceSyncProgressMessage } from '@ozon-ai/ozon/contracts';

function buildFinanceSyncWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/ws/finance-sync`;
}

function parseWsMessage(data: unknown): FinanceSyncProgressMessage | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data) as { type?: string };
    if (parsed?.type !== 'progress') return null;
    return parsed as FinanceSyncProgressMessage;
  } catch {
    return null;
  }
}

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_MS = 1500;

export function useFinanceSyncProgressWs(
  enabled: boolean,
  onMessage: (message: FinanceSyncProgressMessage) => void
): { ready: boolean } {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      ws = new WebSocket(buildFinanceSyncWsUrl());

      ws.onopen = () => {
        reconnectAttempts = 0;
        setReady(true);
      };

      ws.onmessage = (event) => {
        const parsed = parseWsMessage(event.data);
        if (!parsed) return;
        onMessageRef.current(parsed);
      };

      ws.onclose = () => {
        setReady(false);
        ws = null;
        if (stopped || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
        reconnectAttempts += 1;
        reconnectTimer = setTimeout(connect, RECONNECT_MS);
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
      setReady(false);
    };
  }, [enabled]);

  return { ready };
}
